<template>
  <div class="schedule-page">
    <header class="page-hero">
      <div>
        <p class="eyebrow">Contrôle RH</p>
        <h1>Respect horaires</h1>
        <p>Comparer les pointages réels avec le groupe horaire actif de chaque jour.</p>
      </div>
    </header>

    <section class="control-card">
      <label class="field search-field">
        Employé
        <div class="search-wrap">
          <input v-model.trim="searchQuery" type="search" placeholder="Rechercher un employé..." @input="searchStaff" />
        </div>
      </label>

      <div v-if="searchResults.length" class="result-list">
        <button v-for="staff in searchResults" :key="staff.Id" type="button" @click="selectStaff(staff)">
          <img :src="staff.Pic || '/pointage/profile/profile.jpg'" :alt="staff.First_name" />
          <span>
            <strong>{{ staff.First_name }} {{ staff.Last_name || '' }}</strong>
            <small>{{ staff.Post_name }}</small>
          </span>
        </button>
      </div>

      <div class="period-row">
        <label class="field">
          Période
          <select v-model="periodMode" @change="syncPeriodMode">
            <option value="month">Mois</option>
            <option value="range">Du / Au</option>
          </select>
        </label>
        <label v-if="periodMode === 'month'" class="field">
          Mois
          <input v-model="selectedMonth" type="month" @change="loadReport" />
        </label>
        <template v-else>
          <label class="field">
            Du
            <input v-model="periodStart" type="date" @change="loadReport" />
          </label>
          <label class="field">
            Au
            <input v-model="periodEnd" type="date" @change="loadReport" />
          </label>
        </template>
      </div>
    </section>

    <section v-if="selectedStaff" class="employee-strip">
      <img :src="selectedStaff.pic || selectedStaff.Pic || '/pointage/profile/profile.jpg'" :alt="staffName" />
      <div>
        <h2>{{ staffName }}</h2>
        <p>{{ selectedStaff.post_name || selectedStaff.Post_name }} · {{ selectedStaff.group_name || selectedStaff.GroupName || 'Groupe actuel' }}</p>
      </div>
      <button type="button" @click="loadReport" :disabled="loading">
        {{ loading ? 'Chargement...' : 'Actualiser' }}
      </button>
    </section>

    <div v-if="error" class="state-card error">{{ error }}</div>
    <div v-else-if="!selectedStaff" class="state-card">Choisissez un employé pour voir s’il respecte ses horaires.</div>
    <div v-else-if="loading" class="state-card">Chargement du rapport...</div>

    <template v-else-if="report">
      <section class="summary-grid">
        <div>
          <span>Jours</span>
          <strong>{{ summary.total_days }}</strong>
        </div>
        <div class="ok">
          <span>Respectés</span>
          <strong>{{ summary.respected_days }}</strong>
        </div>
        <div class="bad">
          <span>Problèmes</span>
          <strong>{{ problemCount }}</strong>
        </div>
        <div>
          <span>Travaillés</span>
          <strong>{{ summary.worked_days }}</strong>
        </div>
        <div class="bad">
          <span>Coupures</span>
          <strong>{{ summary.days_with_gaps || 0 }}</strong>
        </div>
      </section>

      <section class="quick-filters">
        <button :class="{ active: statusFilter === 'all' }" @click="statusFilter = 'all'">Tous</button>
        <button :class="{ active: statusFilter === 'late' }" @click="statusFilter = 'late'">Retards</button>
        <button :class="{ active: statusFilter === 'early_exit' }" @click="statusFilter = 'early_exit'">Sorties tôt</button>
        <button :class="{ active: statusFilter === 'respected' }" @click="statusFilter = 'respected'">Respectés</button>
        <button :class="{ active: statusFilter === 'gaps' }" @click="statusFilter = 'gaps'">Coupures</button>
        <button :class="{ active: statusFilter === 'no_work' }" @click="statusFilter = 'no_work'">Aucun travail</button>
      </section>

      <section class="day-list">
        <article v-for="day in filteredDays" :key="day.date" class="day-card" :class="day.status">
          <button class="day-summary" type="button" @click="toggleDay(day.date)" :aria-expanded="isDayOpen(day.date)">
            <div>
              <strong>{{ formatDate(day.date) }}</strong>
              <span>{{ day.group_name }} · {{ formatClock(day.scheduled_start) }} → {{ formatClock(day.scheduled_end) }}</span>
            </div>
            <div class="day-summary-right">
              <em :class="summaryClass(day)">{{ compactStatusLabel(day) }}</em>
              <svg :class="{ open: isDayOpen(day.date) }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </button>

          <div class="day-quick-line">
            <span v-if="day.status === 'respected'" class="mini-pill ok">Respecté</span>
            <span v-if="day.status === 'no_work'" class="mini-pill neutral">Aucun travail</span>
            <span v-else-if="day.status === 'incomplete'" class="mini-pill info">Passage ouvert</span>
            <span v-if="day.entry_status === 'late'" class="mini-pill warn">{{ diffLabel(day.entry_diff_minutes, day.entry_status, 'entrée') }}</span>
            <span v-else-if="day.entry_status === 'early'" class="mini-pill early">{{ diffLabel(day.entry_diff_minutes, day.entry_status, 'entrée') }}</span>
            <span v-if="day.exit_status === 'early'" class="mini-pill danger">{{ diffLabel(day.exit_diff_minutes, day.exit_status, 'sortie') }}</span>
            <span v-else-if="day.exit_status === 'late'" class="mini-pill late-exit">{{ diffLabel(day.exit_diff_minutes, day.exit_status, 'sortie') }}</span>
            <span v-if="day.coverage_gaps && day.coverage_gaps.length" class="mini-pill gap">Coupure {{ durationLabel(day.uncovered_minutes) }}</span>
            <span v-if="day.status === 'non_working'" class="mini-pill neutral">{{ day.rh_event_label }}</span>
          </div>

          <div v-if="isDayOpen(day.date)" class="day-details">
            <div v-if="day.status === 'non_working'" class="non-working">
              {{ day.rh_event_label }}
            </div>
            <div v-else-if="day.status === 'no_work'" class="no-work-note">
              Aucun passage enregistré sur l'horaire prévu.
            </div>

            <div v-else class="time-grid">
              <div>
                <span>Entrée prévue</span>
                <strong>{{ formatClock(day.scheduled_start) }}</strong>
              </div>
              <div>
                <span>Entrée réelle</span>
                <strong>{{ formatClock(day.actual_in) || '-' }}</strong>
                <small :class="day.entry_status">{{ diffLabel(day.entry_diff_minutes, day.entry_status, 'entrée') }}</small>
              </div>
              <div>
                <span>Sortie prévue</span>
                <strong>{{ formatClock(day.scheduled_end) }}</strong>
              </div>
              <div>
                <span>Sortie réelle</span>
                <strong>{{ formatClock(day.actual_out) || '-' }}</strong>
                <small :class="day.exit_status">{{ diffLabel(day.exit_diff_minutes, day.exit_status, 'sortie') }}</small>
              </div>
            </div>

            <div v-if="day.coverage_gaps && day.coverage_gaps.length" class="gap-box">
              <div class="detail-title">
                Coupures dans l'horaire prévu
                <strong>{{ durationLabel(day.uncovered_minutes) }}</strong>
              </div>
              <div v-for="gap in day.coverage_gaps" :key="`${day.date}-${gap.start}-${gap.end}`" class="gap-row">
                <span>{{ formatClock(gap.start) }} → {{ formatClock(gap.end) }}</span>
                <strong>{{ durationLabel(gap.minutes) }}</strong>
              </div>
            </div>

            <div v-if="day.session_details && day.session_details.length" class="sessions-box">
              <div class="detail-title">
                Passages du jour
                <strong>{{ day.session_details.length }}</strong>
              </div>
              <div v-for="(session, index) in day.session_details" :key="`${day.date}-session-${index}`" class="session-row">
                <span>Passage {{ index + 1 }}</span>
                <strong>{{ formatClock(session.time_in) }} → {{ session.open ? 'En cours' : formatClock(session.time_out) }}</strong>
                <small v-if="session.covered_minutes > 0">Couvert: {{ formatClock(session.covered_start) }} → {{ formatClock(session.covered_end) }} · {{ durationLabel(session.covered_minutes) }}</small>
                <small v-else-if="!session.open">Hors horaire prévu</small>
                <small v-else>Passage ouvert</small>
              </div>
            </div>
          </div>
        </article>
      </section>
    </template>
  </div>
</template>

<script>
export default {
  name: 'AppScheduleCompliance',
  data() {
    const now = new Date();
    const year = now.getFullYear();
    const monthValue = String(now.getMonth() + 1).padStart(2, '0');
    const dayValue = String(now.getDate()).padStart(2, '0');
    const month = `${year}-${monthValue}`;
    const firstDay = `${year}-${monthValue}-01`;
    const last = new Date(year, now.getMonth() + 1, 0);
    const lastDay = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
    return {
      searchQuery: '',
      searchResults: [],
      selectedStaff: null,
      report: null,
      loading: false,
      error: '',
      searchTimer: null,
      periodMode: 'month',
      selectedMonth: month,
      periodStart: firstDay,
      periodEnd: lastDay || `${year}-${monthValue}-${dayValue}`,
      statusFilter: 'all',
      openDays: {},
    };
  },
  computed: {
    staffName() {
      if (!this.selectedStaff) return '';
      return `${this.selectedStaff.first_name || this.selectedStaff.First_name || ''} ${this.selectedStaff.last_name || this.selectedStaff.Last_name || ''}`.trim();
    },
    summary() {
      return this.report?.summary || {};
    },
    problemCount() {
      return this.summary.problem_days || (this.summary.late_entries || 0) + (this.summary.early_exits || 0) + (this.summary.days_with_gaps || 0) + (this.summary.missing_exits || 0);
    },
    filteredDays() {
      const days = this.report?.days || [];
      if (this.statusFilter === 'all') return days;
      if (this.statusFilter === 'no_work') {
        return days.filter((day) => day.status === 'no_work');
      }
      if (this.statusFilter === 'gaps') {
        return days.filter((day) => day.coverage_gaps && day.coverage_gaps.length);
      }
      if (this.statusFilter === 'late') {
        return days.filter((day) => day.entry_status === 'late');
      }
      if (this.statusFilter === 'early_exit') {
        return days.filter((day) => day.exit_status === 'early');
      }
      return days.filter((day) => day.status === this.statusFilter);
    },
  },
  mounted() {
    const id = Number(this.$route.query.staff_id || 0);
    if (id > 0) {
      this.selectedStaff = { id, first_name: 'Employé', last_name: '' };
      this.loadReport();
    }
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    dateToMonth(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    },
    dateToInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    firstDayOfMonth(date) {
      return this.dateToInput(new Date(date.getFullYear(), date.getMonth(), 1));
    },
    lastDayOfMonth(date) {
      return this.dateToInput(new Date(date.getFullYear(), date.getMonth() + 1, 0));
    },
    syncPeriodMode() {
      this.loadReport();
    },
    searchStaff() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      const query = this.searchQuery.trim();
      if (query.length < 2) {
        this.searchResults = [];
        return;
      }
      this.searchTimer = setTimeout(async () => {
        try {
          const res = await fetch(`/api-pointage/search?query=${encodeURIComponent(query)}`, {
            credentials: 'include',
            cache: 'no-store',
          });
          this.searchResults = res.ok ? await res.json() : [];
        } catch {
          this.searchResults = [];
        }
      }, 220);
    },
    selectStaff(staff) {
      this.selectedStaff = staff;
      this.searchQuery = `${staff.First_name} ${staff.Last_name || ''}`.trim();
      this.searchResults = [];
      this.report = null;
      this.statusFilter = 'all';
      this.openDays = {};
      this.$router.replace({ path: '/respect-horaires', query: { staff_id: staff.Id } });
      this.loadReport();
    },
    async loadReport() {
      const staffID = this.selectedStaff?.Id || this.selectedStaff?.id;
      if (!staffID) return;
      this.loading = true;
      this.error = '';
      const params = new URLSearchParams({ staff_id: String(staffID) });
      if (this.periodMode === 'month') {
        params.set('month', this.selectedMonth);
      } else {
        params.set('start', this.periodStart);
        params.set('end', this.periodEnd);
      }
      try {
        const res = await fetch(`/api-pointage/schedule-compliance?${params.toString()}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.error || 'Erreur rapport horaires');
        this.report = data;
        this.openDays = {};
        if (data.staff) this.selectedStaff = data.staff;
      } catch (err) {
        this.error = err.message;
        this.report = null;
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return '';
      const [year, month, day] = String(value).slice(0, 10).split('-');
      if (!year || !month || !day) return value;
      return `${day}/${month}/${year}`;
    },
    formatClock(value) {
      if (!value) return '';
      const text = String(value).replace('T', ' ');
      const parts = text.split(' ');
      if (parts.length < 2) return text.slice(0, 5);
      return parts[1].slice(0, 5);
    },
    diffLabel(minutes, status, kind) {
      if (status === 'none') return '';
      if (status === 'open') return 'Passage ouvert';
      if (status === 'ok') return 'OK';
      const abs = Math.abs(Number(minutes || 0));
      const h = Math.floor(abs / 60);
      const m = abs % 60;
      const duration = h ? `${h}h${m ? ` ${m}min` : ''}` : `${m}min`;
      if (status === 'early') return kind === 'entrée' ? `Trop tôt ${duration}` : `Sortie tôt ${duration}`;
      if (status === 'late') return kind === 'entrée' ? `Retard ${duration}` : `Sortie tard ${duration}`;
      return '';
    },
    durationLabel(minutes) {
      const value = Math.max(0, Number(minutes || 0));
      const h = Math.floor(value / 60);
      const m = value % 60;
      if (h && m) return `${h}h ${m}min`;
      if (h) return `${h}h`;
      return `${m}min`;
    },
    isDayOpen(date) {
      return Boolean(this.openDays[date]);
    },
    toggleDay(date) {
      this.openDays = {
        ...this.openDays,
        [date]: !this.openDays[date],
      };
    },
    compactStatusLabel(day) {
      if (day.status === 'respected') return 'Respecté';
      if (day.status === 'non_working') return day.rh_event_label || 'Non travaillé';
      if (day.status === 'no_work') return 'Aucun travail';
      if (day.status === 'incomplete') return 'Incomplet';
      if (day.coverage_gaps && day.coverage_gaps.length) return 'Coupure';
      if (day.entry_status === 'late') return 'Retard';
      if (day.exit_status === 'early') return 'Sortie tôt';
      return 'À vérifier';
    },
    summaryClass(day) {
      if (day.status === 'respected') return 'ok';
      if (day.status === 'non_working') return 'neutral';
      if (day.status === 'no_work') return 'neutral';
      if (day.status === 'incomplete') return 'info';
      if (day.coverage_gaps && day.coverage_gaps.length) return 'gap';
      if (day.entry_status === 'late') return 'warn';
      return 'bad';
    },
  },
};
</script>

<style scoped>
.schedule-page {
  display: grid;
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.page-hero,
.control-card,
.employee-strip,
.state-card,
.day-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}

.page-hero {
  padding: 18px 20px;
}

.eyebrow {
  margin: 0 0 5px;
  color: var(--primary);
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

h1,
h2 {
  margin: 0;
  color: var(--gray-900);
}

.page-hero h1 {
  font-size: 26px;
}

.page-hero p:last-child,
.employee-strip p {
  margin: 6px 0 0;
  color: var(--gray-500);
  font-size: 14px;
}

.control-card {
  position: relative;
  display: grid;
  gap: 12px;
  padding: 14px;
}

.period-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.field {
  display: grid;
  gap: 6px;
  color: var(--gray-700);
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.field input,
.field select {
  width: 100%;
  min-height: 42px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  color: var(--gray-900);
  padding: 0 12px;
  font-size: 14px;
  text-transform: none;
}

.result-list {
  display: grid;
  gap: 6px;
  max-height: 260px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px;
}

.result-list button {
  display: grid;
  grid-template-columns: 42px 1fr;
  align-items: center;
  gap: 10px;
  min-height: 54px;
  border: 0;
  border-radius: 7px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  padding: 6px;
}

.result-list button:hover {
  background: var(--gray-50);
}

.result-list img,
.employee-strip img {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--border);
}

.result-list small {
  display: block;
  color: var(--gray-500);
}

.employee-strip {
  display: grid;
  grid-template-columns: 50px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
}

.employee-strip h2 {
  font-size: 18px;
}

.employee-strip button,
.quick-filters button {
  min-height: 38px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: #fff;
  color: var(--gray-800);
  font-weight: 900;
  cursor: pointer;
  padding: 0 12px;
}

.employee-strip button:disabled {
  opacity: 0.55;
}

.state-card {
  padding: 24px;
  color: var(--gray-500);
  text-align: center;
}

.state-card.error {
  color: #dc2626;
  border-color: #fecaca;
  background: #fff5f5;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.summary-grid div {
  min-height: 78px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  padding: 12px;
}

.summary-grid span {
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 800;
}

.summary-grid strong {
  display: block;
  margin-top: 8px;
  color: var(--gray-900);
  font-size: 24px;
}

.summary-grid .ok strong {
  color: #059669;
}

.summary-grid .bad strong {
  color: #dc2626;
}

.quick-filters {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.quick-filters button.active {
  border-color: var(--primary);
  background: var(--primary);
  color: #fff;
}

.day-list {
  display: grid;
  gap: 10px;
}

.day-card {
  padding: 0;
  border-left: 4px solid var(--border);
  overflow: hidden;
}

.day-card.respected {
  border-left-color: #10b981;
}

.day-card.no_work,
.day-card.non_working {
  border-left-color: #94a3b8;
}

.day-card.incomplete {
  border-left-color: #3b82f6;
}

.day-card.coverage_gap {
  border-left-color: #f59e0b;
}

.day-summary {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  border: 0;
  background: #fff;
  padding: 14px;
  text-align: left;
  cursor: pointer;
}

.day-summary strong,
.day-summary span {
  display: block;
}

.day-summary span {
  margin-top: 4px;
  color: var(--gray-500);
  font-size: 13px;
}

.day-summary-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.day-summary-right svg {
  width: 18px;
  height: 18px;
  color: var(--gray-500);
  transition: transform 0.18s ease;
}

.day-summary-right svg.open {
  transform: rotate(180deg);
}

.day-summary em {
  border-radius: 999px;
  background: #eef2ff;
  color: var(--primary);
  font-size: 12px;
  font-style: normal;
  font-weight: 900;
  padding: 6px 9px;
  white-space: nowrap;
}

.day-summary em.ok {
  background: #ecfdf5;
  color: #059669;
}

.day-summary em.bad {
  background: #fef2f2;
  color: #dc2626;
}

.day-summary em.info {
  background: #eff6ff;
  color: #2563eb;
}

.day-summary em.warn {
  background: #fff7ed;
  color: #c2410c;
}

.day-summary em.gap {
  background: #f5f3ff;
  color: #7c3aed;
}

.day-summary em.neutral {
  background: #f1f5f9;
  color: #475569;
}

.day-quick-line {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 0 14px 12px;
}

.mini-pill {
  min-height: 26px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0 9px;
  font-size: 12px;
  font-weight: 900;
  background: #f1f5f9;
  color: #475569;
}

.mini-pill.ok {
  background: #ecfdf5;
  color: #059669;
}

.mini-pill.bad {
  background: #fef2f2;
  color: #dc2626;
}

.mini-pill.warn {
  background: #fffbeb;
  color: #b45309;
}

.mini-pill.early {
  background: #eef2ff;
  color: #4f46e5;
}

.mini-pill.info {
  background: #eff6ff;
  color: #2563eb;
}

.mini-pill.danger {
  background: #fef2f2;
  color: #dc2626;
}

.mini-pill.late-exit {
  background: #fff7ed;
  color: #c2410c;
}

.mini-pill.gap {
  background: #f5f3ff;
  color: #7c3aed;
}

.mini-pill.neutral {
  background: #f1f5f9;
  color: #475569;
}

.day-details {
  border-top: 1px solid var(--border);
  padding: 12px 14px 14px;
  background: #fbfdff;
}

.time-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.time-grid div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--gray-50);
  padding: 10px;
}

.time-grid span,
.time-grid small {
  display: block;
  color: var(--gray-500);
  font-size: 11px;
  font-weight: 800;
}

.time-grid strong {
  display: block;
  margin: 5px 0;
  color: var(--gray-900);
  font-size: 16px;
}

.time-grid small.ok {
  color: #059669;
}

.time-grid small.early {
  color: #4f46e5;
}

.time-grid small.late {
  color: #b45309;
}

.time-grid small.open {
  color: #2563eb;
}

.non-working,
.no-work-note,
.sessions-note {
  margin-top: 12px;
  color: var(--gray-600);
  font-size: 13px;
}

.gap-box,
.sessions-box {
  display: grid;
  gap: 8px;
  margin-top: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  padding: 10px;
}

.gap-box {
  border-color: #fecaca;
  background: #fff7f7;
}

.detail-title,
.gap-row,
.session-row {
  display: grid;
  gap: 4px;
}

.detail-title {
  grid-template-columns: 1fr auto;
  align-items: center;
  color: var(--gray-900);
  font-size: 13px;
  font-weight: 900;
}

.detail-title strong {
  color: #dc2626;
}

.gap-row,
.session-row {
  grid-template-columns: 1fr auto;
  align-items: start;
  border-top: 1px solid rgba(148, 163, 184, 0.22);
  padding-top: 8px;
  color: var(--gray-700);
  font-size: 13px;
}

.gap-row strong {
  color: #dc2626;
}

.session-row small {
  grid-column: 1 / -1;
  color: var(--gray-500);
  font-size: 12px;
}

@media (max-width: 760px) {
  .page-hero {
    padding: 14px;
  }

  .page-hero h1 {
    font-size: 23px;
  }

  .period-row,
  .summary-grid,
  .time-grid {
    grid-template-columns: 1fr 1fr;
  }

  .employee-strip {
    grid-template-columns: 44px 1fr;
  }

  .employee-strip button {
    grid-column: 1 / -1;
    width: 100%;
  }

  .day-summary {
    padding: 12px;
  }

  .day-quick-line {
    padding: 0 12px 12px;
  }

  .day-details {
    padding: 12px;
  }
}

@media (max-width: 430px) {
  .period-row,
  .summary-grid,
  .time-grid {
    grid-template-columns: 1fr;
  }

  .day-summary {
    display: grid;
  }

  .day-summary-right {
    justify-content: space-between;
    width: 100%;
  }
}
</style>
